import React, { useState, useEffect } from 'react';
import {
  Users, IndianRupee, Calendar, Pencil, Printer,
  FileText, Receipt, Loader2, ArrowRight, Star, X, AlertCircle,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { usePermission } from '@/hooks/usePermission';
import {
  getPatient, getPatientInvoices, getPatientPrescriptions,
  getSixMonthsAgoDate, type QueueEntry,
} from '@/lib/api';
import { displayAge } from '@/lib/patientAge';

interface Props {
  patientId: string;
  patientName: string;
  queueEntry: (QueueEntry & { doctorName: string }) | null;
  onNewInvoice: (patient: any) => void;
  onCollectPayment: (invoice: any) => void;
  onBookAppointment: (patient: any) => void;
  onEditPatient: (patient: any) => void;
  onPrintPrescription: (rx: any) => void;
  onPrintInvoice: (inv: any) => void;
  onPriority: (entry: QueueEntry) => void;
  onRemoveFromQueue: (entry: QueueEntry) => void;
  onViewFullHistory: (patientId: string) => void;
}

function formatDate(dateStr: string) {
  if (!dateStr) return '';
  return new Date(dateStr + (dateStr.includes('T') ? '' : 'T00:00:00')).toLocaleDateString('en-IN', {
    day: 'numeric', month: 'short', year: 'numeric',
  });
}

function RemoveConfirm({ name, onConfirm, onCancel }: { name: string; onConfirm: () => void; onCancel: () => void }) {
  return (
    <div className="flex items-center gap-2">
      <span className="text-xs text-destructive font-medium">Remove {name}?</span>
      <button
        onClick={onConfirm}
        className="px-2 py-0.5 rounded text-[11px] font-semibold bg-destructive text-destructive-foreground hover:bg-destructive/90"
      >
        Yes
      </button>
      <button
        onClick={onCancel}
        className="px-2 py-0.5 rounded text-[11px] font-semibold bg-muted text-muted-foreground hover:bg-muted/80"
      >
        No
      </button>
    </div>
  );
}

export default function PatientContextPanel({
  patientId,
  patientName,
  queueEntry,
  onNewInvoice,
  onCollectPayment,
  onBookAppointment,
  onEditPatient,
  onPrintPrescription,
  onPrintInvoice,
  onPriority,
  onRemoveFromQueue,
  onViewFullHistory,
}: Props) {
  const { can } = usePermission();
  const [patient, setPatient] = useState<any>(null);
  const [invoices, setInvoices] = useState<any[]>([]);
  const [prescriptions, setPrescriptions] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [confirmRemove, setConfirmRemove] = useState(false);

  useEffect(() => {
    if (!patientId) return;
    setLoading(true);
    setPatient(null);
    setInvoices([]);
    setPrescriptions([]);

    const from = getSixMonthsAgoDate();
    Promise.all([
      getPatient(patientId),
      can('billing:read') ? getPatientInvoices(patientId, { from }) : Promise.resolve([]),
      can('prescriptions:read') ? getPatientPrescriptions(patientId, { from }) : Promise.resolve([]),
    ]).then(([p, inv, rx]) => {
      setPatient(p);
      setInvoices(Array.isArray(inv) ? inv : []);
      setPrescriptions(Array.isArray(rx) ? rx : []);
    }).catch(() => {}).finally(() => setLoading(false));
  }, [patientId]);

  const outstanding = invoices
    .filter(inv => inv.payment_status === 'unpaid' || inv.payment_status === 'partial')
    .reduce((sum, inv) => sum + (Number(inv.total_amount) - Number(inv.paid_amount || 0)), 0);

  // Merge invoices + prescriptions into a unified visit timeline, last 5 entries
  const recentItems = React.useMemo(() => {
    const items: { date: string; type: 'invoice' | 'rx'; data: any }[] = [
      ...invoices.map(inv => ({ date: (inv.invoice_date || inv.created_at || '').split('T')[0], type: 'invoice' as const, data: inv })),
      ...prescriptions.map(rx => ({ date: (rx.date || rx.created_at || '').split('T')[0], type: 'rx' as const, data: rx })),
    ];
    return items.sort((a, b) => b.date.localeCompare(a.date)).slice(0, 6);
  }, [invoices, prescriptions]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-48">
        <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const p = patient ?? { name: patientName };
  const displayName = p.name ?? patientName;

  return (
    <div className="p-4 space-y-3">
      {/* Patient header */}
      <div className="rounded-lg border border-border bg-card p-4">
        <div className="flex items-start justify-between gap-3 mb-3">
          <div className="min-w-0">
            <h2 className="text-base font-bold capitalize">{displayName}</h2>
            <p className="text-xs text-muted-foreground mt-0.5">
              {[
                displayAge(p.age, p.date_of_birth),
                p.gender,
                p.phone,
                p.mrn && `MRN: ${p.mrn}`,
              ].filter(Boolean).join(' · ')}
            </p>
            {p.last_visit_date && (
              <p className="text-xs text-muted-foreground mt-0.5">
                Last visit: {formatDate(p.last_visit_date)}
              </p>
            )}
          </div>
          {outstanding > 0 && (
            <div className="text-right flex-shrink-0">
              <p className="text-[10px] text-muted-foreground">Outstanding</p>
              <p className="text-lg font-bold text-destructive">₹{outstanding.toLocaleString('en-IN')}</p>
            </div>
          )}
        </div>

        {/* Action buttons */}
        <div className="flex flex-wrap gap-2">
          {can('billing:write') && (
            <Button size="sm" className="h-7 text-xs gap-1" onClick={() => onNewInvoice(p)}>
              <IndianRupee className="w-3 h-3" /> New Invoice
            </Button>
          )}
          {can('appointments:write') && (
            <Button size="sm" variant="outline" className="h-7 text-xs gap-1" onClick={() => onBookAppointment(p)}>
              <Calendar className="w-3 h-3" /> Book Appt
            </Button>
          )}
          {can('patients:write') && (
            <Button size="sm" variant="ghost" className="h-7 text-xs gap-1 text-muted-foreground" onClick={() => onEditPatient(p)}>
              <Pencil className="w-3 h-3" /> Edit
            </Button>
          )}
          <Button
            size="sm"
            variant="ghost"
            className="h-7 text-xs gap-1 text-primary ml-auto"
            onClick={() => onViewFullHistory(patientId)}
          >
            Full history <ArrowRight className="w-3 h-3" />
          </Button>
        </div>
      </div>

      {/* Queue status */}
      {queueEntry && (
        <div className={`rounded-lg border p-3 ${
          queueEntry.status === 'in_consultation'
            ? 'bg-primary/5 border-primary/30'
            : queueEntry.status === 'waiting'
            ? 'bg-warning/5 border-warning/30'
            : 'bg-success/5 border-success/30'
        }`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold ${
                queueEntry.status === 'in_consultation' ? 'bg-primary text-primary-foreground' :
                queueEntry.status === 'waiting' ? 'bg-warning text-warning-foreground' :
                'bg-success text-success-foreground'
              }`}>
                {queueEntry.tokenNumber}
              </div>
              <div>
                <p className="text-xs font-semibold">
                  {queueEntry.status === 'in_consultation' && '● In Consultation'}
                  {queueEntry.status === 'waiting' && `Waiting${queueEntry.waitingTimeMinutes ? ` · ${queueEntry.waitingTimeMinutes} min` : ''}`}
                  {queueEntry.status === 'completed' && '✓ Completed'}
                </p>
                {queueEntry.doctorName && (
                  <p className="text-[11px] text-muted-foreground">
                    {queueEntry.doctorName.startsWith('Dr.') ? queueEntry.doctorName : `Dr. ${queueEntry.doctorName}`}
                  </p>
                )}
              </div>
            </div>
            {/* Queue actions — only for waiting */}
            {queueEntry.status === 'waiting' && can('queue:write') && (
              <div className="flex gap-1.5 items-center">
                {confirmRemove ? (
                  <RemoveConfirm
                    name={queueEntry.patientName}
                    onConfirm={() => { setConfirmRemove(false); onRemoveFromQueue(queueEntry); }}
                    onCancel={() => setConfirmRemove(false)}
                  />
                ) : (
                  <>
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-7 text-xs gap-1"
                      onClick={() => onPriority(queueEntry)}
                    >
                      <Star className="w-3 h-3" /> Priority
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-7 w-7 p-0 text-destructive hover:text-destructive hover:bg-destructive/10"
                      onClick={() => setConfirmRemove(true)}
                      title="Remove from queue"
                    >
                      <X className="w-3.5 h-3.5" />
                    </Button>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Recent visits */}
      {recentItems.length > 0 && (
        <div className="rounded-lg border border-border overflow-hidden">
          <div className="px-3 py-2 bg-muted/30 border-b border-border">
            <p className="text-xs font-semibold text-muted-foreground">Recent activity (6 months)</p>
          </div>
          <div className="divide-y divide-border">
            {recentItems.map((item, i) => (
              <div key={i} className="flex items-center gap-3 px-3 py-2.5">
                {item.type === 'invoice' ? (
                  <Receipt className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
                ) : (
                  <FileText className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    {item.type === 'invoice' ? (
                      <>
                        <span className="text-xs font-medium">₹{Number(item.data.total_amount).toLocaleString('en-IN')}</span>
                        <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${
                          item.data.payment_status === 'paid' ? 'bg-success/10 text-success' :
                          item.data.payment_status === 'partial' ? 'bg-warning/10 text-warning' :
                          'bg-destructive/10 text-destructive'
                        }`}>
                          {item.data.payment_status}
                        </span>
                      </>
                    ) : (
                      <span className="text-xs font-medium">
                        Prescription{item.data.diagnosis ? ` · ${item.data.diagnosis}` : ''}
                      </span>
                    )}
                  </div>
                  <p className="text-[11px] text-muted-foreground">{formatDate(item.date)}</p>
                </div>
                <div className="flex gap-1 flex-shrink-0">
                  {item.type === 'invoice' && (
                    <>
                      {can('billing:write') && (item.data.payment_status === 'unpaid' || item.data.payment_status === 'partial') && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-6 text-[11px] px-2 text-warning border-warning/30 hover:bg-warning/10"
                          onClick={() => onCollectPayment(item.data)}
                        >
                          Collect
                        </Button>
                      )}
                      <button
                        onClick={() => onPrintInvoice(item.data)}
                        className="p-1 rounded hover:bg-muted text-muted-foreground"
                        title="Print invoice"
                      >
                        <Printer className="w-3.5 h-3.5" />
                      </button>
                    </>
                  )}
                  {item.type === 'rx' && can('prescriptions:read') && (
                    <button
                      onClick={() => onPrintPrescription(item.data)}
                      className="p-1 rounded hover:bg-muted text-muted-foreground"
                      title="Print prescription"
                    >
                      <Printer className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {!loading && recentItems.length === 0 && (
        <div className="text-center py-8">
          <AlertCircle className="w-8 h-8 mx-auto text-muted-foreground/30 mb-2" />
          <p className="text-xs text-muted-foreground">No visits in the last 6 months</p>
        </div>
      )}
    </div>
  );
}
